import React, { useMemo, useState } from "react";
import { CalendarDaysIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";

import Layout from "../../components/layout/Layout";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  Spinner,
  Textarea,
} from "../../components/ui";
import {
  CreatePulseWorkshopRequest,
  PULSE_TRACKS,
  PulseTrack,
  useCreatePulseWorkshop,
  usePulseWorkshops,
} from "../../hooks/usePulse";

const initialForm: CreatePulseWorkshopRequest = {
  organization_name: "",
  workshop_date: "",
  tracks: ["engineering"],
  referral_context: "",
  internal_notes: "",
};
const WORKSHOP_PAGE_SIZE = 50;

function formatWorkshopDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00`));
}

function trackLabel(track: PulseTrack): string {
  return PULSE_TRACKS.find((option) => option.value === track)?.label ?? track;
}

export default function PulsePage() {
  const navigate = useNavigate();
  const [workshopOffset, setWorkshopOffset] = useState(0);
  const workshopsQuery = usePulseWorkshops(WORKSHOP_PAGE_SIZE, workshopOffset);
  const createWorkshop = useCreatePulseWorkshop();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreatePulseWorkshopRequest>(initialForm);

  const isValid = useMemo(
    () =>
      Boolean(
        form.organization_name.trim() &&
        form.workshop_date &&
        form.tracks.length,
      ),
    [form],
  );

  const toggleTrack = (track: PulseTrack, checked: boolean) => {
    setForm((current) => ({
      ...current,
      tracks: checked
        ? [...current.tracks, track]
        : current.tracks.filter((value) => value !== track),
    }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) return;
    const workshop = await createWorkshop.mutateAsync({
      ...form,
      organization_name: form.organization_name.trim(),
      referral_context: form.referral_context?.trim() || null,
      internal_notes: form.internal_notes?.trim() || null,
    });
    setShowCreate(false);
    setForm(initialForm);
    navigate(`/pulse/workshops/${workshop.id}`);
  };

  return (
    <Layout>
      <Page maxWidth="xl">
        <PageHeader
          title="PULSE"
          description="Workshop Intelligence System for pre-workshop alignment and facilitator readiness."
          actions={
            <Button variant="brand" onClick={() => setShowCreate(true)}>
              <PlusIcon />
              Create workshop
            </Button>
          }
        />
        <PageBody className="space-y-4">
          {workshopsQuery.isError && (
            <Alert variant="error">Unable to load PULSE workshops.</Alert>
          )}

          {workshopsQuery.isLoading ? (
            <div className="flex min-h-64 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : workshopsQuery.data?.items.length ? (
            <Panel>
              <PanelBody className="divide-y divide-gray-100 p-0 dark:divide-dark-border">
                {workshopsQuery.data.items.map((workshop) => (
                  <Link
                    key={workshop.id}
                    to={`/pulse/workshops/${workshop.id}`}
                    className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-eliza-red dark:hover:bg-dark-surface-2"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-subtitle text-base text-charcoal dark:text-gray-100">
                          {workshop.organization_name}
                        </span>
                        <Badge
                          variant={
                            workshop.status === "open" ? "success" : "secondary"
                          }
                        >
                          {workshop.status === "open" ? "Open" : "Closed"}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {workshop.tracks.map((track) => (
                          <Badge key={track} variant="outline">
                            {trackLabel(track)}
                          </Badge>
                        ))}
                        <Badge variant="secondary">
                          {workshop.included_submission_count ?? 0} responses
                        </Badge>
                        <Badge
                          variant={
                            workshop.report_status === "current"
                              ? "success"
                              : workshop.report_status === "stale"
                                ? "warning"
                                : "secondary"
                          }
                        >
                          Brief: {workshop.report_status ?? "missing"}
                        </Badge>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Creator user {workshop.created_by_user_id ?? "unknown"}{" "}
                        · Latest activity{" "}
                        {formatWorkshopDate(
                          (
                            workshop.latest_activity_at ?? workshop.updated_at
                          ).slice(0, 10),
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <CalendarDaysIcon className="h-4 w-4" />
                      {formatWorkshopDate(workshop.workshop_date)}
                    </div>
                  </Link>
                ))}
                <div className="flex items-center justify-between gap-4 px-6 py-4">
                  <span className="text-xs text-gray-500">
                    Showing {workshopOffset + 1}–
                    {workshopOffset + workshopsQuery.data.items.length} of{" "}
                    {workshopsQuery.data.total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={workshopOffset === 0}
                      onClick={() =>
                        setWorkshopOffset((current) =>
                          Math.max(0, current - WORKSHOP_PAGE_SIZE),
                        )
                      }
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={
                        workshopOffset + workshopsQuery.data.items.length >=
                        workshopsQuery.data.total
                      }
                      onClick={() =>
                        setWorkshopOffset(
                          (current) => current + WORKSHOP_PAGE_SIZE,
                        )
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          ) : (
            <Panel>
              <PanelBody className="py-16 text-center">
                <h2 className="font-title text-2xl text-charcoal dark:text-gray-100">
                  Create the first workshop
                </h2>
                <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500 dark:text-gray-400">
                  Add the organization, date, and tracks. PULSE will create one
                  survey link for the team.
                </p>
                <Button
                  className="mt-6"
                  variant="brand"
                  onClick={() => setShowCreate(true)}
                >
                  Create workshop
                </Button>
              </PanelBody>
            </Panel>
          )}
        </PageBody>
      </Page>

      <Modal open={showCreate} onClose={() => setShowCreate(false)}>
        <ModalContent size="lg">
          <form onSubmit={submit}>
            <ModalHeader>
              <ModalTitle>Create PULSE workshop</ModalTitle>
              <ModalDescription>
                This creates the internal workspace and its public survey link.
              </ModalDescription>
            </ModalHeader>
            <ModalBody className="space-y-5">
              {createWorkshop.error && (
                <Alert variant="error">
                  The workshop could not be created. Check the details and try
                  again.
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="pulse-organization">Organization</Label>
                <Input
                  id="pulse-organization"
                  autoFocus
                  required
                  value={form.organization_name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      organization_name: event.target.value,
                    }))
                  }
                  placeholder="Acme Labs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pulse-date">Workshop date</Label>
                <Input
                  id="pulse-date"
                  type="date"
                  required
                  value={form.workshop_date}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      workshop_date: event.target.value,
                    }))
                  }
                />
              </div>
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium text-charcoal dark:text-gray-200">
                  Tracks offered
                </legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {PULSE_TRACKS.map((track) => (
                    <Checkbox
                      key={track.value}
                      label={track.label}
                      checked={form.tracks.includes(track.value)}
                      onChange={(event) =>
                        toggleTrack(track.value, event.target.checked)
                      }
                    />
                  ))}
                </div>
              </fieldset>
              <div className="space-y-2">
                <Label htmlFor="pulse-referral">OpenAI referral context</Label>
                <Textarea
                  id="pulse-referral"
                  rows={3}
                  value={form.referral_context ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      referral_context: event.target.value,
                    }))
                  }
                  placeholder="Optional context from the referral or discovery call"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pulse-notes">Internal notes</Label>
                <Textarea
                  id="pulse-notes"
                  rows={3}
                  value={form.internal_notes ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      internal_notes: event.target.value,
                    }))
                  }
                  placeholder="Optional facilitator notes"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand"
                disabled={!isValid || createWorkshop.isPending}
              >
                {createWorkshop.isPending ? "Creating…" : "Create workshop"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Layout>
  );
}
